import React, {useState} from 'react';
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import WhatsOnIcon from "../../icons/WhatsOnIcon";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Divider from "@mui/material/Divider";
import SideMenu from "../../components/SideMenu";
import {sideMenuDefaultsMap} from "../../maps/sideMenuDefaultsMap";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import {useTheme, styled} from "@mui/material/styles";
import ProfileImage from "../../components/ProfileImage";
import AccountInfoDialog from "../../components/dialogs/AccountInfoDialog";
import {isMobileUserAgent} from "../../utils";
import {colors} from "../../maps/colors";

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

export const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

export const ProfileAvatarStyled = styled('div')(() => ({
    cursor: "pointer"
}));

export const WhatsOnIconWrapper = styled('div')(() => ({
    width: "190px",
    height: "50px",
    '@media (max-width: 780px)': {
        width: '100px',
        height: 'auto',
        marginTop: 7
    }
}));

export const AvatarWrapper = styled('div')(() => ({
    position: "absolute",
    top: "16px",
    right: "20px",
    textAlign: "right",
    fontSize: "12px",
    alignItems: "center",
    '@media (max-width: 780px)': {
        top: '9px'
    }
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const CommonLayout = ({children, sideMenuData, account}) => {
    const theme = useTheme();
    const [open, s_open] = useState(false);
    const [accountInfoIsOpen, s_accountInfoIsOpen] = useState(false);

    const handleDrawerOpen = () => {
        s_open(true);
    };

    const handleDrawerClose = () => {
        s_open(false);
    };

    return (
        <Box sx={{display: 'flex', overflow: 'none'}}>
            <CssBaseline/>
            <AppBar position="fixed" open={open}
                    style={{background: colors.GRAY3, border: 'none'}}
            >
                <Toolbar>
                    <IconButton
                        style={{marginRight: 4}}
                        color="black"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: 4.5,
                            transition: 'padding 0.4s ease-in',
                            ...(open && {width: 0, marginRight: 1.5, padding: 0, overflow: 'hidden'}),
                        }}
                    >
                        <MenuIcon/>
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        <WhatsOnIconWrapper>
                            <WhatsOnIcon/>
                        </WhatsOnIconWrapper>
                        <AvatarWrapper>
                            <ProfileAvatarStyled
                                onClick={() => {
                                    s_accountInfoIsOpen(true);
                                }}
                            >
                                <ProfileImage
                                    imageUrl={account.imageUrl}
                                    name={account.name}
                                />
                            </ProfileAvatarStyled>
                        </AvatarWrapper>
                    </Typography>
                </Toolbar>
            </AppBar>
            <UseDrawer
                open={open}
            >
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                    </IconButton>
                </DrawerHeader>
                <Divider/>
                <SideMenu open={open} data={sideMenuData}/>
                <Divider/>
                <SideMenu open={open} data={sideMenuDefaultsMap}/>
            </UseDrawer>
            {children}
            {accountInfoIsOpen && (
                <AccountInfoDialog
                    account={account}
                    open={accountInfoIsOpen}
                    onClose={() => {
                        s_accountInfoIsOpen(false);
                    }}
                />
            )}
        </Box>
    );
};

export default CommonLayout;

const UseDrawer = ({children, open}) => {
    const isMobile = isMobileUserAgent();
    if (isMobile) {
        return (
            <MuiDrawer
                sx={{
                    width: 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                {children}
            </MuiDrawer>
        );
    }
    return (
        <Drawer
            variant={isMobile ? "persistent" : "permanent"} open={open}
        >
            {children}
        </Drawer>
    );
};